import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import type * as CSS from 'csstype';
import { Avatar, Box, BoxProps, IconButton, Text, createButtonColorConfig } from '@99designs/emu';
import { boxShadows, breakpoints, colors, spaces } from '@99designs/emu/settings';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { IconHeart1, IconTrash2 } from '@99designs/icons';
import { linkTo } from '@99designs/routing';
import ConfirmRemoveModal from './ConfirmRemoveModal';
import type { Design, SelectionState, UpdateType } from '../../types';

type BorderWidth = 'thick' | 'thin' | 'none';

const Border = styled.div<{ width: BorderWidth }>`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    ${({ width }) => {
        switch (width) {
            case 'thick':
                return `border: 4px solid ${colors.grey500}`;
            case 'thin':
                return `border: 1px solid ${colors.grey300}`;
            default:
                return 'border: none';
        }
    }};

    border-radius: ${spaces['0.5bu']};
`;

const RoundedCoverImage = styled.img`
    object-fit: cover;
    border-radius: ${spaces['0.5bu']};
`;

const DesignWrapper = styled(Box)<{ isClickable: boolean }>`
    ${({ isClickable }) =>
        isClickable &&
        `
        cursor: pointer;
    `}
`;

const OverlayContainer = styled(Box)<{ canShow: boolean }>`
    background-color: ${colors.withAlpha(colors.grey800, 0)};
    border: none;
    border-radius: ${spaces['0.5bu']};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: background-color 0.3s ease;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    opacity: 0;

    ${({ canShow }) =>
        canShow &&
        `
        opacity: 1;
        background-color: ${colors.withAlpha(colors.grey900, 0.65)};
        box-shadow: ${boxShadows.low};
    `}
`;

export interface DynamicDesignCardProps {
    src: string;
    alt?: string;
    displayName: string;
    designerId: string;
    designId?: number;
    liked: boolean;
    minWidth?: BoxProps['width'];
    minHeight?: BoxProps['height'];
    variant: SelectionState;
    onDesignUpdated: (design: Design, updateType: UpdateType) => void;
}

export function DynamicDesignCard({
    src,
    alt,
    displayName,
    designerId,
    designId,
    liked = false,
    minWidth,
    minHeight,
    variant,
    onDesignUpdated,
}: DynamicDesignCardProps) {
    const [isMouseOver, setIsMouseOver] = useState(false);
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: breakpoints.small[1] });

    function getIconColor(): CSS.Property.Color | colors.Color {
        if (liked) {
            return colors.experimental.red600;
        }
        if (isMouseOver) {
            return 'grey700';
        }

        return 'grey600';
    }

    function getBgColor(): CSS.Property.Color | colors.Color {
        if (liked) {
            return colors.experimental.red200;
        }
        if (isMouseOver) {
            return 'grey200';
        }

        return 'grey100';
    }

    function getBorderWidth(): BorderWidth {
        if (variant === 'BROWSE') {
            if (liked) {
                return 'thick';
            }
            if (isMouseOver) {
                return 'none';
            }
        }
        return 'thin';
    }

    function updateDesign(update: UpdateType): void {
        onDesignUpdated(
            { image: src, id: designId, designer: { displayName, id: designerId } },
            update
        );
    }

    function handleLike(): void {
        if (variant === 'BROWSE') {
            updateDesign(liked ? 'REMOVE' : 'ADD');
        }
    }

    function handleConfirmRemove(): void {
        updateDesign('REMOVE');
        setShowConfirmRemove(false);
    }

    return (
        <Box>
            <DesignWrapper
                minWidth={minWidth}
                minHeight={minHeight}
                width="auto"
                height="auto"
                onClick={handleLike}
                position="relative"
                display="inline-block"
                onMouseEnter={() => setIsMouseOver(true)}
                onMouseLeave={() => setIsMouseOver(false)}
                isClickable={variant === 'BROWSE'}
            >
                <RoundedCoverImage
                    src={src}
                    className="lazyload"
                    alt={alt || formatMessage(__('Design by {displayName}'), { displayName })}
                />

                <Border width={getBorderWidth()} />

                <Box position="absolute" top="0.5bu" right="0.5bu" zIndex={1}>
                    {variant === 'BROWSE' ? (
                        <IconButton
                            appearance={createButtonColorConfig({
                                normal: {
                                    backgroundColor: getBgColor(),
                                    backgroundColorHover: getBgColor(),
                                    backgroundColorActive: getBgColor(),
                                    borderColor: 'grey100',
                                    borderColorHover: 'grey100',
                                    borderColorActive: 'grey100',
                                    textColor: getIconColor(),
                                    textColorHover: getIconColor(),
                                    textColorActive: getIconColor(),
                                },
                            })}
                            aria-label={__placeholder('Shortlist')}
                            icon={IconHeart1}
                            size="small"
                            noTooltip
                        />
                    ) : (
                        <IconButton
                            appearance="secondary"
                            aria-label={__placeholder('Remove')}
                            icon={IconTrash2}
                            size="small"
                            onClick={() => setShowConfirmRemove(true)}
                        />
                    )}
                </Box>

                <OverlayContainer canShow={isMouseOver && variant === 'BROWSE'}>
                    <Box
                        position="absolute"
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        bottom="0.5bu"
                        left="0.5bu"
                    >
                        <Box display={['none', 'block', 'block', 'block']} marginRight="0.5bu">
                            <Avatar url={linkTo.userAvatar(designerId)} size="extra-small" />
                        </Box>
                        <Text fontFamily="attribution" size="size2" italic color={colors.white}>
                            {isMobile
                                ? displayName
                                : formatMessage(__('by {designerName}'), {
                                      designerName: displayName,
                                  })}
                        </Text>
                    </Box>
                </OverlayContainer>
            </DesignWrapper>
            <ConfirmRemoveModal
                visible={showConfirmRemove}
                onConfirm={handleConfirmRemove}
                onCancel={() => setShowConfirmRemove(false)}
            />
        </Box>
    );
}
